import React, { useEffect, useState } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Chip,
  Avatar,
  useDisclosure,
} from "@nextui-org/react";

import {
  getAlldata,
  getDataWithFilterUrl,
  getFilterDataWithPagination,
} from "../../controllers/APIController";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import LoadingPage from "../../components/Loader/LoadingPage";
import NullData from "../../components/ErrorPage/NullData";
import { useNavigate, useParams } from "react-router-dom";
import InviteModal from "./InviteModal";
import UserDrawer from "./UserDrawer/UserDrawer";
import { timeAgo } from "../../utils/helper";

/**
 * UserManagement Component
 * Main component for managing team members, their contributions, and access permissions
 * Provides user listing, contribution tracking, and invitation management
 * @returns {JSX.Element} User management interface
 */
const UserManagement = () => {
  // URL parameters for current organization
  const { _orgId: currentOrgId } = useParams();

  // Component state
  const [contributionCount, setContributionCount] = useState({});
  const [userData, setUserData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [invitationList, setInvitationList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Navigation and user data
  const navigate = useNavigate();
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  const orgInfo = userLogged && userLogged.user.LastOrgId;
  const orgFilterUrl = "filters[Organization][id][$eqi]=" + orgInfo;

  // Available colors for user avatars
  const avatarColors = ["secondary", "success", "warning", "danger", "primary"];
  const numColors = avatarColors.length;

  // Modal and drawer disclosure hooks
  const {
    isOpen: isModalOpen,
    onOpen: onModalOpen,
    onOpenChange: onModalOpenChange,
  } = useDisclosure();

  const {
    isOpen: isDrawerOpen,
    onOpen: onDrawerOpen,
    onOpenChange: onDrawerOpenChange,
  } = useDisclosure();

  /**
   * Fetch users for the current organization
   * Loads all users and filters them to show only members of the current organization
   */
  useEffect(() => {
    setIsLoading(true);
    getAlldata("users")
      .then((data) => {
        const orgUserList = data?.filter((user) => {
          const organizationLists = user?.OrganizationLists;
          let isMemberOfCurrentOrg = false;
          
          organizationLists.forEach((org) => {
            if (org.id === orgInfo) {
              isMemberOfCurrentOrg = true;
            }
          });
          
          return isMemberOfCurrentOrg;
        });
        
        setUserData(orgUserList);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
        setIsLoading(false);
      });
  }, [orgInfo]);

  /**
   * Calculate contribution count for each user
   * Fetches response data to count user contributions to the organization
   */
  useEffect(() => {
    const contributionCounts = {};
    
    const fetchContributions = userData?.map(async (user) => {
      const filterUrl = orgFilterUrl + "&filters[users_permissions_user][id][$eqi]=" + user?.id;
      
      try {
        const response = await getFilterDataWithPagination(
          "responses",
          filterUrl,
          1,
          1
        );
        
        contributionCounts[user.id] = response?.meta?.pagination?.total || 0;
        return response;
      } catch (error) {
        console.error(`Error fetching contributions for user ${user.id}:`, error);
        contributionCounts[user.id] = 0;
      }
    });

    Promise.all(fetchContributions).then(() => {
      setContributionCount(contributionCounts);
    });
  }, [userData, orgFilterUrl]);

  /**
   * Fetch pending invitations for the current organization
   * Loads invitation data to show pending team member requests
   */
  useEffect(() => {
    getDataWithFilterUrl(
      "invitations",
      `filters[organization][id][$eqi]=${orgInfo}&filters[Status][$eqi]=Pending`
    ).then((data) => {
      const emailList = data?.data?.map((item) => item?.attributes.email);
      setInvitationList(emailList);
    }).catch((error) => {
      console.error("Error fetching invitations:", error);
    });
  }, [orgInfo]);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <div className="bg-white border-b border-slate-200 px-8 py-6 shadow-sm">
        <div className="flex items-center justify-between max-w-full">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">User Management</h1>
            <p className="text-sm text-slate-600 mt-1">Manage team members and their access</p>
          </div>
          <Button 
            color="primary" 
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 font-medium"
            onPress={onModalOpen}
          >
            Invite your team
          </Button>
        </div>
      </div>

      <BreadCrumbsPage>
        <div className="max-w-7xl mx-auto px-8 py-8">
          <LoadingPage isLoading={isLoading} loadingText="Loading team members...">
            {userData.length > 0 ? (
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                {/* Table Header */}
                <div className="px-6 py-4 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">Team Members</h3>
                      <p className="text-sm text-slate-600 mt-1">
                        Manage team members and their access. You can invite other users to contribute to your ESG reporting.
                        <span
                          className="text-emerald-600 cursor-pointer hover:text-emerald-700 font-medium ml-1"
                          onClick={() => navigate(`/${currentOrgId}/invite-request`)}
                        >
                          View invitation requests
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Team Members Table */}
                <Table
                  removeWrapper
                  aria-label="Team members table"
                  selectionMode="single"
                  classNames={{
                    th: "bg-slate-50 text-slate-700 font-medium",
                    td: "py-4",
                  }}
                >
                  <TableHeader>
                    <TableColumn>Name</TableColumn>
                    <TableColumn>Contributions</TableColumn>
                    <TableColumn>Last Login At</TableColumn>
                    <TableColumn>Status</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {userData && userData.length > 0
                      ? userData.map((user) => {
                          const colorIndex = user.id % numColors;
                          const lastLoginAt = user?.lastLoginAt
                            ? timeAgo(+user?.lastLoginAt)
                            : "long time ago";
                          
                          return (
                            <TableRow
                              key={user.id}
                              onClick={() => {
                                setSelectedUser(user);
                                onDrawerOpen();
                              }}
                              className="cursor-pointer"
                            >
                              {/* User Name and Avatar */}
                              <TableCell>
                                <div className="flex flex-row items-center gap-4">
                                  <Avatar
                                    src={user?.ProfilePictureUrl || ""}
                                    color={avatarColors[colorIndex]}
                                    name={
                                      user?.email?.slice(0, 1).toUpperCase() || ""
                                    }
                                    size="sm"
                                  />
                                  <div className="flex flex-col">
                                    <p className="">
                                      {user.email && user.email.split("@")[0]}
                                    </p>
                                    <p className="text-slate-400">
                                      {user.username}
                                    </p>
                                  </div>
                                </div>
                              </TableCell>
                              
                              {/* Contribution Count */}
                              <TableCell>
                                {contributionCount[user?.id] === undefined
                                  ? "--"
                                  : contributionCount[user?.id]}
                              </TableCell>
                              
                              {/* Last Login Time */}
                              <TableCell>{lastLoginAt}</TableCell>
                              
                              {/* User Status */}
                              <TableCell>
                                <Chip variant="flat" color="success">
                                  Active
                                </Chip>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      : null}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-12">
                <NullData />
              </div>
            )}
          </LoadingPage>

          {/* Invite Team Modal */}
          <InviteModal
            UserData={userData}
            isOpen={isModalOpen}
            onOpen={onModalOpen}
            onOpenChange={onModalOpenChange}
            invitationList={invitationList}
          />
          
          {/* User Details Drawer */}
          <UserDrawer
            OrgInfo={orgInfo}
            User={selectedUser}
            isOpen={isDrawerOpen}
            onOpen={onDrawerOpen}
            onOpenChange={onDrawerOpenChange}
          />
        </div>
      </BreadCrumbsPage>
    </div>
  );
};

export default UserManagement;
